import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    FunctionField,
    ReferenceField,
    ShowButton,
    EditButton,
    Filter,
    TextInput,
    SearchInput,
    SelectInput,
    DateInput,
    ReferenceInput,
    FormDataConsumer,
    TopToolbar,
    ExportButton,
    RefreshButton,
    useTranslate,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import {
    Box,
    Chip,
    Typography,
    Tooltip,
    Fab,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    CalendarMonth,
    EventAvailable,
    HourglassEmpty,
    CheckCircle,
    Cancel,
    Block,
    Warning,
    BatteryChargingFull,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

type ReservationStatus =
    | 'pending_payment'
    | 'confirmed'
    | 'active'
    | 'completed'
    | 'cancelled'
    | 'no_show'
    | 'expired';

const STATUS_STYLES: Record<ReservationStatus, {
    color: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary';
    variant: 'outlined' | 'filled';
    icon: React.ReactNode;
}> = {
    pending_payment: { color: 'warning', variant: 'outlined', icon: <HourglassEmpty fontSize="small" /> },
    confirmed: { color: 'success', variant: 'outlined', icon: <CheckCircle fontSize="small" /> },
    active: { color: 'primary', variant: 'filled', icon: <BatteryChargingFull fontSize="small" /> },
    completed: { color: 'success', variant: 'filled', icon: <CheckCircle fontSize="small" /> },
    cancelled: { color: 'default', variant: 'outlined', icon: <Block fontSize="small" /> },
    no_show: { color: 'error', variant: 'outlined', icon: <Warning fontSize="small" /> },
    expired: { color: 'default', variant: 'outlined', icon: <Cancel fontSize="small" /> },
};

export const ReservationStatusChip: React.FC<{ status: string }> = ({ status }) => {
    const translate = useTranslate();
    const s = STATUS_STYLES[status as ReservationStatus] || STATUS_STYLES.pending_payment;
    return (
        <Chip
            icon={s.icon as any}
            variant={s.variant}
            color={s.color as any}
            label={translate(`resources.reservations.statuses.${status}`, { _: status })}
            size="small"
            sx={{ height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal' } }}
        />
    );
};

const FeeAmountField: React.FC<{ record: any }> = ({ record }) => {
    const amount = record.fee_amount ?? record.feeAmount;
    if (amount === null || amount === undefined) return <span>-</span>;
    return <span style={{ fontFamily: 'monospace' }}>{`HKD ${Number(amount).toFixed(2)}`}</span>;
};

const WalletHoldStatusChip: React.FC<{ status: string | null | undefined }> = ({ status }) => {
    const translate = useTranslate();
    if (!status) return <span>-</span>;
    const colorMap: Record<string, 'default' | 'warning' | 'primary' | 'success' | 'info'> = {
        pending: 'warning',
        held: 'primary',
        released: 'success',
        captured: 'info',
    };
    return (
        <Chip
            size="small"
            color={colorMap[status] || 'default'}
            label={translate(`resources.reservations.walletHoldStatus.${status}`, { _: status })}
        />
    );
};

// Filters live as an array (React Admin v5 modern form). Two design choices
// for visual consistency across the row:
//   1. variant="outlined" + size="small" on every input — same border style and
//      height (40px).
//   2. InputLabelProps={{ shrink: true }} everywhere — DateInputs need this to
//      keep their label out of the `dd/mm/yyyy` placeholder, so we apply it to
//      ALL inputs so labels float above uniformly. Without this the date
//      inputs end up taller than the others.
const SHARED_INPUT_PROPS = {
    variant: 'outlined' as const,
    size: 'small' as const,
    InputLabelProps: { shrink: true },
};

const useReservationsFilters = () => {
    const translate = useTranslate();
    return [
        <TextInput
            key="search"
            source="search"
            label={translate('resources.reservations.filters.search')}
            alwaysOn
            {...SHARED_INPUT_PROPS}
        />,
        <ReferenceInput
            key="site"
            source="site_id"
            reference="sites"
            perPage={500}
            alwaysOn
        >
            <SelectInput
                optionText="name"
                label={translate('resources.reservations.fields.siteId')}
                {...SHARED_INPUT_PROPS}
            />
        </ReferenceInput>,
        // Charger filter depends on the selected site. FormDataConsumer reads
        // the current filter form values so the dropdown can be filtered AND
        // disabled with a helper text when nothing is picked.
        <FormDataConsumer key="charger" source="charger_id" alwaysOn>
            {({ formData }: any) => {
                const selectedSite = formData?.site_id;
                return (
                    <ReferenceInput
                        source="charger_id"
                        reference="chargers"
                        perPage={500}
                        filter={selectedSite ? { site_id: selectedSite } : {}}
                    >
                        <SelectInput
                            optionText="name"
                            label={translate('resources.reservations.fields.chargerId')}
                            disabled={!selectedSite}
                            {...SHARED_INPUT_PROPS}
                        />
                    </ReferenceInput>
                );
            }}
        </FormDataConsumer>,
        <ReferenceInput
            key="appUser"
            source="app_user_id"
            reference="app-users"
            perPage={500}
            alwaysOn
        >
            <SelectInput
                optionText="email"
                label={translate('resources.reservations.fields.appUserId')}
                {...SHARED_INPUT_PROPS}
            />
        </ReferenceInput>,
        <SelectInput
            key="status"
            source="status"
            label={translate('resources.reservations.fields.status')}
            alwaysOn
            {...SHARED_INPUT_PROPS}
            choices={[
                { id: 'pending_payment', name: translate('resources.reservations.statuses.pending_payment') },
                { id: 'confirmed', name: translate('resources.reservations.statuses.confirmed') },
                { id: 'active', name: translate('resources.reservations.statuses.active') },
                { id: 'completed', name: translate('resources.reservations.statuses.completed') },
                { id: 'cancelled', name: translate('resources.reservations.statuses.cancelled') },
                { id: 'no_show', name: translate('resources.reservations.statuses.no_show') },
                { id: 'expired', name: translate('resources.reservations.statuses.expired') },
            ]}
        />,
        <SelectInput
            key="mode"
            source="reservation_mode"
            label={translate('resources.reservations.fields.reservationMode')}
            alwaysOn
            {...SHARED_INPUT_PROPS}
            choices={[
                { id: 'fixed_slot', name: translate('resources.reservations.modes.fixed_slot') },
                { id: 'next_slot', name: translate('resources.reservations.modes.next_slot') },
            ]}
        />,
        <DateInput
            key="from"
            source="start_time_from"
            label={translate('resources.reservations.filters.fromDate')}
            alwaysOn
            {...SHARED_INPUT_PROPS}
        />,
        <DateInput
            key="to"
            source="start_time_to"
            label={translate('resources.reservations.filters.toDate')}
            alwaysOn
            {...SHARED_INPUT_PROPS}
        />,
    ];
};

const MobileReservationsFilter = (props: any) => (
    <Filter {...props}>
        <SearchInput source="search" alwaysOn />
    </Filter>
);

// Default React Admin export dumps every field on the record (including
// internal blobs like policy_snapshot and ids no operator needs) with raw ISO
// timestamps and machine enum values. This exporter curates the columns,
// formats dates to local time, joins fee amount + currency, and translates
// status / mode / fee policy to human-readable values so the CSV opens cleanly
// in Excel/Sheets.
const useReservationsExporter = () => {
    const translate = useTranslate();
    return React.useCallback(
        async (records: any[]) => {
            const fmtDate = (iso?: string | null): string => {
                if (!iso) return '';
                const d = new Date(iso);
                return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
            };
            const fmtEnum = (group: string, value?: string | null): string => {
                if (!value) return '';
                return translate(`resources.reservations.${group}.${value}`, { _: value });
            };
            const fmtFee = (amount?: number | null, currency?: string | null): string => {
                if (amount === null || amount === undefined) return '';
                return `${currency || 'HKD'} ${Number(amount).toFixed(2)}`;
            };
            const rows = records.map((r: any) => ({
                'Reservation ID': r.id ?? '',
                'Status': fmtEnum('statuses', r.status),
                'User Name': r.user_name ?? '',
                'User Email': r.user_email ?? '',
                'Site': r.site_name ?? '',
                'Charger': r.charger_name ?? '',
                'Connector': r.connector_id ?? '',
                'Mode': fmtEnum('modes', r.reservation_mode),
                'Start Time': fmtDate(r.start_time),
                'Slot End': fmtDate(r.slot_end_at),
                'Hold Until': fmtDate(r.hold_until),
                'Duration (min)': r.duration_minutes ?? '',
                'Fee Policy': fmtEnum('feePolicies', r.fee_policy),
                'Fee Amount': fmtFee(r.fee_amount, r.fee_currency),
                'Wallet Hold Status': r.wallet_hold_status ?? '',
                'Cancelled At': fmtDate(r.cancelled_at),
                'Cancelled Reason': r.cancelled_reason ?? '',
                'Created At': fmtDate(r.created_at),
            }));

            const { downloadCSV } = await import('react-admin');
            const headers = Object.keys(rows[0] ?? {});
            // Minimal RFC-4180 CSV: quote any field containing a comma,
            // newline, or quote; double up embedded quotes.
            const escape = (val: unknown): string => {
                const s = val === null || val === undefined ? '' : String(val);
                return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
            };
            const csvLines = [
                headers.map(escape).join(','),
                ...rows.map((row: any) => headers.map((h) => escape(row[h])).join(',')),
            ];
            const csv = csvLines.join('\n');
            const stamp = new Date().toISOString().slice(0, 10);
            downloadCSV(csv, `reservations-${stamp}`);
        },
        [translate],
    );
};

// Bump maxResults to 5000 to match the backend's per-request cap. The default
// is 1000 which previously got further clamped to 100 server-side, silently
// truncating any export of more than a screen of results.
const RESERVATIONS_EXPORT_MAX = 5000;

const DesktopListActions = () => (
    <TopToolbar>
        <ExportButton maxResults={RESERVATIONS_EXPORT_MAX} />
        <RefreshButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0, gap: 1 }}>
        <ExportButton maxResults={RESERVATIONS_EXPORT_MAX} />
        <RefreshButton />
    </Box>
);

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        onClick={() => navigate(`/reservations/${record.id}/show`)}
                        sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <EventAvailable fontSize="small" color="primary" />
                            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {String(record.id).slice(-8)}
                            </Typography>
                            <ReservationStatusChip status={record.status} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                                {record.start_time ? new Date(record.start_time).toLocaleString() : '-'}
                            </Typography>
                        </Box>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const ReservationsList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const reservationFilters = useReservationsFilters();
    const exporter = useReservationsExporter();

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={isMobile ? <MobileReservationsFilter /> : reservationFilters}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                sort={{ field: 'start_time', order: 'DESC' }}
                perPage={25}
                empty={isMobile ? false : undefined}
                title={translate('resources.reservations.list.title')}
                exporter={exporter}
                sx={{
                    // Render the filter bar as a single tidy flex row that
                    // wraps onto multiple lines on narrower viewports. Each
                    // input has a fixed minimum width so the columns align
                    // even when the row wraps.
                    '& .RaFilterForm-root': {
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                        rowGap: 1.5,
                        columnGap: 1.5,
                        minHeight: 0,
                        py: 1,
                    },
                    // Constrain every filter input to the same width so the
                    // bar reads as a grid, not a ragged sequence.
                    '& .RaFilterForm-root .filter-field': {
                        margin: 0,
                    },
                    '& .RaFilterForm-root .MuiFormControl-root': {
                        width: 200,
                        marginTop: 0,
                        marginBottom: 0,
                    },
                    '& .RaFilterForm-root .MuiInputBase-root': {
                        height: 40,
                    },
                }}
            >
                {isMobile ? (
                    <MobileList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        rowClick="show"
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 'bold',
                            },
                        }}
                    >
                        <FunctionField
                            label={translate('resources.reservations.fields.id')}
                            source="id"
                            render={(record: any) => (
                                <Tooltip title={String(record.id)}>
                                    <span style={{ fontFamily: 'monospace' }}>{String(record.id).slice(-8)}</span>
                                </Tooltip>
                            )}
                        />
                        <FunctionField
                            label={translate('resources.reservations.fields.status')}
                            source="status"
                            render={(record: any) => <ReservationStatusChip status={record.status} />}
                        />
                        <ReferenceField
                            source="site_id"
                            reference="sites"
                            label={translate('resources.reservations.fields.siteId')}
                            link={false}
                        >
                            <TextField source="name" />
                        </ReferenceField>
                        <ReferenceField
                            source="charger_id"
                            reference="chargers"
                            label={translate('resources.reservations.fields.chargerId')}
                            link={false}
                        >
                            <TextField source="name" />
                        </ReferenceField>
                        <FunctionField
                            label={translate('resources.reservations.fields.connectorId')}
                            source="connector_id"
                            render={(record: any) => record.connector_id ?? record.connectorId ?? '-'}
                        />
                        <DateField
                            source="start_time"
                            label={translate('resources.reservations.fields.startTime')}
                            showTime
                            locales="en-GB"
                        />
                        <FunctionField
                            label={translate('resources.reservations.fields.feePolicy')}
                            source="fee_policy"
                            render={(record: any) => {
                                const p = record.fee_policy || record.feePolicy;
                                if (!p) return '-';
                                return (
                                    <Tooltip title={translate(`resources.reservations.feePolicyTooltips.${p}`, { _: '' })}>
                                        <Chip size="small" variant="outlined" label={translate(`resources.reservations.feePolicies.${p}`, { _: p })} />
                                    </Tooltip>
                                );
                            }}
                        />
                        <FunctionField
                            label={translate('resources.reservations.fields.walletHoldStatus')}
                            source="wallet_hold_status"
                            render={(record: any) => <WalletHoldStatusChip status={record.wallet_hold_status || record.walletHoldStatus} />}
                        />
                        <FunctionField
                            label={translate('resources.reservations.fields.feeAmount')}
                            source="fee_amount"
                            render={(record: any) => <FeeAmountField record={record} />}
                        />
                        <FunctionField
                            label=""
                            source="actions"
                            render={(record: any) => (
                                <Box display="flex" gap={0}>
                                    <Tooltip title={translate('ra.action.show')}>
                                        <span><ShowButton record={record} size="small" label="" /></span>
                                    </Tooltip>
                                    <Tooltip title={translate('ra.action.edit')}>
                                        <span><EditButton record={record} size="small" label="" /></span>
                                    </Tooltip>
                                </Box>
                            )}
                            textAlign="right"
                        />
                    </Datagrid>
                )}
            </List>
            <Fab
                color="primary"
                onClick={() => navigate('/reservations/calendar')}
                sx={{ position: 'fixed', bottom: 24, right: 24, backgroundColor: '#009A75', '&:hover': { backgroundColor: '#007A5E' }, zIndex: 1100 }}
                title={translate('resources.reservations.calendar.title')}
            >
                <CalendarMonth />
            </Fab>
        </Box>
    );
};

export default ReservationsList;
